import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import type { PostingTemplateDto } from "@/features/ledger/api/dto";
import { PostingTemplateRowActions } from "@/features/ledger/components/posting-templates/posting-template-row-actions";
import {
  POSTING_TEMPLATE_STATUSES,
  type PostingTemplateStatus,
} from "@/features/shared/api/enums";

/** Ligne de la liste : `DataTableClar` exige un `id` requis (optionnel dans le DTO). */
export type PostingTemplateRow = PostingTemplateDto & { id: string };

const STATUS_VARIANTS: Record<
  PostingTemplateStatus,
  "warning" | "success" | "secondary"
> = {
  DRAFT: "warning",
  ACTIVE: "success",
  ARCHIVED: "secondary",
};

/**
 * Colonnes des modèles d'imputation (`POST /PostingTemplates/all`).
 * Filtres : `productCodeId`, `providerId` (texte) et `status` (select).
 */
export const postingTemplatesColumns: ColumnsClarFactory<PostingTemplateRow> = (
  navigate
) => [
  {
    id: "productCodeId",
    accessorKey: "productCodeId",
    header: t("ledger.templates.columns.productCodeId"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "providerId",
    accessorKey: "providerId",
    header: t("ledger.templates.columns.providerId"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "version",
    accessorKey: "version",
    header: t("ledger.templates.columns.version"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{String(getValue() ?? "—")}</NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("ledger.templates.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as PostingTemplateStatus | undefined;
      return status ? (
        <NoTranslate as="span">
          <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>{status}</Badge>
        </NoTranslate>
      ) : (
        "—"
      );
    },
    meta: {
      type: "select",
      options: POSTING_TEMPLATE_STATUSES.map((value) => ({
        value,
        label: value,
      })),
    },
  },
  {
    id: "lines",
    accessorKey: "lines",
    header: t("ledger.templates.columns.lines"),
    cell: ({ getValue }) => {
      const lines = getValue() as PostingTemplateDto["lines"] | undefined;
      return <NoTranslate as="span">{lines?.length ?? 0}</NoTranslate>;
    },
    meta: { type: "denied" },
  },
  {
    id: "effectiveFrom",
    accessorKey: "effectiveFrom",
    header: t("ledger.templates.columns.effectiveFrom"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "effectiveTo",
    accessorKey: "effectiveTo",
    header: t("ledger.templates.columns.effectiveTo"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <PostingTemplateRowActions template={row.original} navigate={navigate} />
      ) : null,
    meta: { type: "denied" },
  },
];
